import type { CrossPlotAxis } from './utils';
import type { CrossPlotChartConfig } from '../types';
import { camelizeDeep } from './saved-well-filter-definition';

/**
 * Round-trip helpers for a persisted cross plot's `definition` jsonb. A cross
 * plot is a `TypeCurve::AnalogWellFilter` row with `kind='cross_plot'` whose
 * `name` is the client chart id and whose `definition` carries the chart config.
 * On WRITE the backend deep-underscores the keys; on READ jbuilder camelizes
 * only the top-level response keys, so the nested `definition` reads back
 * snake_case and must be {@link camelizeDeep}d first.
 */

// Chart config -> `definition` payload (camelCase; backend underscores the keys).
export function crossPlotToDefinition(
  chart: CrossPlotChartConfig
): Record<string, unknown> {
  return {
    title: chart.title,
    xAxis: chart.xAxis,
    yAxis: chart.yAxis,
    visible: chart.visible,
  };
}

// Stored row (name + definition) -> chart config. `id` is the row's `name` (the
// original client chart id), so client identity survives a round-trip.
export function definitionToCrossPlot(
  name: string,
  definition: Record<string, unknown>
): CrossPlotChartConfig {
  const d = camelizeDeep(definition ?? {}) as {
    title?: string;
    xAxis?: CrossPlotAxis;
    yAxis?: CrossPlotAxis;
    visible?: boolean;
  };
  return {
    id: name,
    title: d.title ?? '',
    xAxis: d.xAxis as CrossPlotAxis,
    yAxis: d.yAxis as CrossPlotAxis,
    visible: d.visible ?? true,
  };
}
